import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";

const BlogFiduciaireVsComptable = () => {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Article Header */}
        <article>
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-['Manrope'] leading-tight">
              Fiduciaire ou comptable interne : que choisir pour votre PME suisse ?
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              Le comparatif honnête que votre expert-comptable ne vous fera pas
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                Performance Transmission
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                8 Mars 2026
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                7 min de lecture
              </span>
            </div>
          </header>

          {/* Article Content */}
          <div className="prose prose-lg prose-gray max-w-none">
            <p className="lead text-gray-600 text-lg leading-relaxed">
              C'est l'une des premières décisions structurantes d'un chef de PME : est-ce qu'on gère la comptabilité 
              en interne ou on confie ça à une fiduciaire ? Les arguments des uns et des autres sont souvent 
              influencés par leur propre intérêt.
            </p>
            <p className="text-gray-600">
              Cet article présente un comparatif objectif, chiffré, adapté aux PME suisses de 1 à 30 employés. 
              L'objectif : vous aider à prendre la bonne décision pour votre situation.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4 font-['Manrope']">
              Ce que couvre réellement la fonction comptable dans une PME
            </h2>
            <p className="text-gray-600">
              Avant de comparer les options, délimitons le périmètre. La comptabilité d'une PME suisse comprend typiquement :
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 my-4">
              <li>La saisie et le suivi des factures clients et fournisseurs</li>
              <li>Le rapprochement bancaire mensuel</li>
              <li>La gestion de la TVA (décomptes trimestriels ou semestriels)</li>
              <li>La paie et les charges sociales (AVS, LPP, assurances)</li>
              <li>La clôture annuelle et l'établissement des comptes</li>
              <li>La déclaration fiscale de la société</li>
              <li>Le conseil courant (optimisation fiscale, questions RH)</li>
            </ul>
            <p className="text-gray-600">
              La charge varie de 4-5h/mois pour une micro-entreprise simple à 20-30h/mois pour une PME avec paie 
              complexe, stock et projets multiples.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4 font-['Manrope']">
              Le comptable interne : quand ça a du sens
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3 font-['Manrope']">
              Avantages réels
            </h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 my-4">
              <li><strong>Disponibilité immédiate</strong> — il connaît le contexte de chaque transaction</li>
              <li><strong>Réactivité</strong> — une question urgente se règle en cinq minutes</li>
              <li><strong>Connaissance approfondie</strong> de l'entreprise après 6 mois</li>
              <li><strong>Gestion des tâches administratives connexes</strong> (commandes, relances, notes de frais)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3 font-['Manrope']">
              Limites souvent sous-estimées
            </h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 my-4">
              <li><strong>Coût réel élevé</strong> : un comptable à 60-70% coûte 50'000 à 65'000 CHF/an charges comprises</li>
              <li><strong>Dépendance à une seule personne</strong> : congé maladie, départ — qui reprend ?</li>
              <li><strong>Expertise limitée</strong> : maîtrise la routine, rarement l'optimisation fiscale avancée</li>
              <li><strong>Risque d'erreur sans regard externe</strong> : les anomalies peuvent s'accumuler sans être détectées</li>
            </ul>
            <p className="text-gray-600 bg-gray-50 p-4 rounded-lg border-l-4 border-blue-600">
              <strong>Conclusion :</strong> pertinent à partir de 15-20 employés avec des flux complexes et réguliers.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4 font-['Manrope']">
              La fiduciaire : ce qu'on sous-estime souvent
            </h2>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3 font-['Manrope']">
              Avantages concrets
            </h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 my-4">
              <li><strong>Coût maîtrisé</strong> : 3'000 à 8'000 CHF/an pour une PME de 1-10 personnes</li>
              <li><strong>Expertise multiple</strong> : spécialistes TVA, paie, fiscal selon les besoins</li>
              <li><strong>Continuité garantie</strong> : pas de dépendance à une seule personne</li>
              <li><strong>Regard extérieur</strong> : détection anomalies, optimisations fiscales, conformité légale</li>
              <li><strong>Pas de charges sociales ni de gestion RH</strong> liée au poste</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3 font-['Manrope']">
              Limites réelles
            </h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 my-4">
              <li>Moins de réactivité au quotidien (24-48h de délai typique)</li>
              <li>Qualité variable selon les fiduciaires</li>
              <li>Nécessite une bonne organisation de votre côté</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4 font-['Manrope']">
              Le comparatif chiffré
            </h2>
            
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse border border-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Critère</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Fiduciaire</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Comptable interne</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600">
                  <tr>
                    <td className="border border-gray-200 px-4 py-3">Coût annuel (1-10 pers.)</td>
                    <td className="border border-gray-200 px-4 py-3 font-semibold text-blue-600">3'000 – 8'000 CHF</td>
                    <td className="border border-gray-200 px-4 py-3">50'000 – 65'000 CHF</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3">Coût annuel (10-30 pers.)</td>
                    <td className="border border-gray-200 px-4 py-3 font-semibold text-blue-600">8'000 – 15'000 CHF</td>
                    <td className="border border-gray-200 px-4 py-3">60'000 – 80'000 CHF</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-3">Réactivité quotidienne</td>
                    <td className="border border-gray-200 px-4 py-3">Moyenne (24-48h)</td>
                    <td className="border border-gray-200 px-4 py-3 font-semibold text-blue-600">Excellente</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3">Expertise fiscale avancée</td>
                    <td className="border border-gray-200 px-4 py-3 font-semibold text-blue-600">Incluse</td>
                    <td className="border border-gray-200 px-4 py-3">Limitée</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-3">Risque dépendance</td>
                    <td className="border border-gray-200 px-4 py-3 font-semibold text-blue-600">Faible</td>
                    <td className="border border-gray-200 px-4 py-3">Élevé</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3">Continuité si absence</td>
                    <td className="border border-gray-200 px-4 py-3 font-semibold text-blue-600">Garantie</td>
                    <td className="border border-gray-200 px-4 py-3">Problématique</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-3">Flexibilité du périmètre</td>
                    <td className="border border-gray-200 px-4 py-3 font-semibold text-blue-600">Modulable</td>
                    <td className="border border-gray-200 px-4 py-3">Fixe</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4 font-['Manrope']">
              Le modèle hybride : une option souvent oubliée
            </h2>
            <p className="text-gray-600">
              Beaucoup de PME en croissance adoptent un modèle mixte : une assistante administrative qui gère 
              la saisie courante, et une fiduciaire pour la TVA, les clôtures et la fiscalité.
            </p>
            <p className="text-gray-600">
              Ce modèle fonctionne particulièrement bien entre 10 et 20 employés. Il permet de conserver la 
              réactivité quotidienne sans supporter le coût d'un comptable qualifié à temps plein.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4 font-['Manrope']">
              Les situations où chaque option s'impose
            </h2>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3 font-['Manrope']">
              Optez pour une fiduciaire si :
            </h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 my-4">
              <li>Vous avez moins de 15 employés et des flux réguliers et bien organisés</li>
              <li>Vous venez de créer votre entreprise et cherchez à maîtriser vos coûts fixes</li>
              <li>Vous avez besoin d'un accompagnement fiscal, pas seulement de la saisie</li>
              <li>Vous avez déjà subi les aléas d'un départ de comptable interne</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3 font-['Manrope']">
              Optez pour un comptable interne si :
            </h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 my-4">
              <li>Votre activité génère des transactions quotidiennes nombreuses et complexes</li>
              <li>Vous dépassez 20 employés avec des paies complexes (horaires variables, commissions)</li>
              <li>Votre secteur nécessite une présence permanente et intégrée</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4 font-['Manrope']">
              Conclusion
            </h2>
            <p className="text-gray-600">
              La décision dépend de trois facteurs : votre volume de transactions, votre besoin de réactivité 
              quotidienne, et le niveau d'expertise requis au-delà de la saisie.
            </p>
            <p className="text-gray-600">
              Pour la très grande majorité des PME suisses de moins de 15 personnes, une fiduciaire bien choisie 
              est la solution la plus rentable et la plus sécurisante. La clé : choisir une fiduciaire 
              <strong> proactive</strong> — pas une qui attend que vous lui envoyiez vos documents trois semaines 
              avant la clôture.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-gray-900 font-semibold text-lg mb-4">
              Performance Transmission accompagne les PME suisses de 1 à 30 employés en comptabilité, 
              paie et déclarations fiscales.
            </p>
            <p className="text-gray-600 mb-6">
              Premier entretien sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
              >
                <a href="mailto:compta@performancetransmission.ch">
                  Nous contacter
                </a>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 rounded-full px-6"
              >
                <Link to="/#contact">
                  Prendre rendez-vous
                </Link>
              </Button>
            </div>
          </div>
        </article>
      </main>
      
      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © 2025 Performance Transmission Sàrl. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
};

export default BlogFiduciaireVsComptable;
